import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListSubheader,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Editor } from "@tinymce/tinymce-react";
import Moment from "moment";
import toast from "../../utils/toast.jsx";
import { motion } from "framer-motion";
import HelpHint from "../parts/helpHint";
import { useNavigate, useLocation } from "react-router-dom";
import { UsersApi } from "../../services/api/users";
import { CollectionsApi } from "../../services/api/api";
import { EmailsApi } from "../../services/api/emails";
import { DistributionsApi } from "../../services/api/distributions";
import TierDropdown from "../parts/tierDropdown";

Moment.locale("en");

function DistributionAdd(props) {
  const { token } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modified, setModified] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [emails, setEmails] = useState(location.state?.emailList || []);
  const [manualEmail, setManualEmail] = useState("");
  const [time, setTime] = useState(Moment());
  const [toggleSchedule, setToggleSchedule] = useState(false);

  const [collections, setCollections] = useState([]);
  const [groupedSets, setGroupedSets] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState({});

  const [emailList, setEmailList] = useState([]);
  const [iTiers, setITiers] = useState({});

  const [version, setVersion] = useState(location.state?.version || "new");
  const [monthlyMode, setMonthlyMode] = useState(null); // No mode selected initially
  const [modeToggled, setModeToggled] = useState(false);
  const [step, setStep] = useState(0);
  const [activeMonthSets, setActiveMonthSets] = useState([]);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [templateName, setTemplateName] = useState("");

  // Missing state variables
  const [collectionDisabled, setCollectionDisabled] = useState(false);
  const [enableSubmitFlag, setEnableSubmitFlag] = useState(true);
  const [warning, setWarning] = useState(false);
  const [message, setMessage] = useState("");
  const [notes, setNotes] = useState("");
  const [collectionsIndex, setCollectionsIndex] = useState([]);
  const [viewOnly, setViewOnly] = useState(false);

  const isMounted = useRef(true);

  // Keep track of original tier ID for recursive tier processing
  // let originalId = null;

  useEffect(() => {
    isMounted.current = true;
    const beforeUnload = (e) => {
      if (modified) {
        e.preventDefault();
        e.returnValue = "Are you sure you want to stop creating this distribution?";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);

    getPhotoListEndpoint();
    getEmailListEndpoint();
    getTemplates();

    return () => {
      isMounted.current = false;
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, []);

  const getTemplates = async () => {
    try {
      const response = await UsersApi.getTemplates(props.token);

      if (response && response.success) {
        console.log('Templates API response:', response);

        // The response.data should be an array with structure: [{ data: [...], templatesByName: [...] }]
        let templates = [];

        if (response.data && response.data.length > 0 && response.data[0].data) {
          templates = response.data[0].data; // This should be the array of [name, content] pairs
        }

        // Add "No Template" option at the beginning
        templates.unshift(["No Template", "", true]);

        console.log('Processed templates:', templates);

        setTemplates(templates);

        // Set default template if available
        const defaultIndex = templates.findIndex(t => t[0].toLowerCase() === 'default');
        if (defaultIndex > 0) {
          setSelectedTemplate(defaultIndex);
          setTemplateName(templates[defaultIndex][0]);
          setBody(templates[defaultIndex][1]);
        }
      } else {
        console.warn("Error getting email templates - API response not successful");
        toast.warn("Error getting email templates", 10000);
      }
    } catch (err) {
      console.error("Error getting templates", err);
      toast.warn("Something went wrong getting templates", 10000);
    }
  };

  const getPhotoListEndpoint = async () => {
    if (isMounted.current) {
      try {
        let token = props.token;
        if (typeof token === 'function') {
          token = await token();
        }
        if (!token || typeof token !== 'string' || token === 'undefined') {
          toast.error('No valid token found for distribution API call.');
          return;
        }

        const response = await CollectionsApi.getCollections(token);

        if (response && response.success) {
          console.log('CollectionsApi response structure:', response);
          console.log('Response data:', response.data);
          console.log('Type of response.data:', typeof response.data);

          let list = [];
          let zList = [];
          let groups = [];
          let groupedSets = [];

          // Check if response.data has collectionlist or if response.data IS the collectionlist
          const collectionlist = response.data.collectionlist || response.data;
          console.log('Collectionlist:', collectionlist);
          console.log('Number of collections found:', collectionlist ? collectionlist.length : 0);

          if (collectionlist && Array.isArray(collectionlist)) {
            console.log('All collections with their statuses:');
            collectionlist.forEach((item, index) => {
              console.log(`${index + 1}. "${item.collectionName}" - Status: "${item.collectionStatus}"`);
            });

            collectionlist.forEach((item) => {
              console.log('Processing collection:', item.collectionName, 'Status:', item.collectionStatus);

              // Temporarily include all collections for debugging - you can change this back later
              const includeItem = item.collectionStatus === "Ready for Distribution" ||
                item.collectionStatus === "Processing" ||
                item.collectionStatus === "Complete" ||
                true; // This will include all collections for now

              // Fixed: Allow collections without collectionStatus (new collections)
              if (includeItem) {
                let activeMonth;

                if (item.activeMonth) {
                  activeMonth = item.activeMonth;
                } else activeMonth = 0;

                let list_item = {
                  title: item.collectionName,
                  relId: item.relId,
                  date: item.createdAt,
                  tier: item.tier,
                  activeMonth: activeMonth,
                  disabled: false,
                  selected: false,
                  status: item.collectionStatus, // Add status for debugging
                  isLinked: item.isLinked === true,
                  linkType: item.linkType || null,
                };

                if (!groups.includes("g" + activeMonth)) {
                  groups.push("g" + activeMonth);
                }

                zList[item.relId] = list_item;
                list.push(list_item);
              }
            });
          } else {
            console.log('No collectionlist found or invalid format');
          } groups.sort().reverse();
          groups.forEach((item) => {
            groupedSets[item] = { 0: "", 1: [] };
          });

          list.forEach((item) => {
            if (item.relId !== 0) {
              groupedSets["g" + item.activeMonth][0] = "g" + item.activeMonth;
              if (!groupedSets["g" + item.activeMonth][1])
                groupedSets["g" + item.activeMonth][1] = [];
              groupedSets["g" + item.activeMonth][1].push(item);
            }
          });

          groupedSets = Object.values(groupedSets);

          console.log('Loaded collections:', list.length, 'collections');
          console.log('Grouped sets:', groupedSets.length, 'groups');
          console.log('Sample collections:', list.slice(0, 3));
          console.log('Sample grouped sets:', groupedSets.slice(0, 2));

          setCollections(list);
          setGroupedSets(groupedSets);
        } else {
          console.log("Failed to get collection list");
        }
      } catch (err) {
        console.log("Error getting collection list", err);
      }
    }
  };

  const getEmailListEndpoint = async () => {
    try {
      const response = await EmailsApi.getEmails(props.token);

      if (response && response.success) {
        let list = [];

        response.data.tierlist.forEach((item) => {
          let list_item = {
            title: item.relId,
            relId: item.relId,
            nickname: item.tierName,
            color: item.color,
            parentTier: item.parentTier,
          };

          list.push(list_item);
        });

        let ztier = {};
        list.forEach((listItem) => {
          let id = listItem.relId;
          ztier[id] = listItem;
        });

        let emailList = [];

        response.data.emaillist.forEach((item) => {
          let tier = parseInt(item.tier);

          // Check if the tier exists in our tier map
          if (ztier[tier]) {
            const tier_rel_id = ztier[tier].relId;
            const tcolor = ztier[tier].color;
            const tn = ztier[tier].nickname;

            let list_item = {
              id: item.id,
              note: item.note,
              tier: tn,
              tier_rel_id: tier_rel_id,
              email: item.email,
              color: tcolor,
            };

            emailList.push(list_item);
          } else {
            console.warn(`Tier ${tier} not found in tier list for email ${item.email}`);
          }
        });

        if (isMounted.current) {
          console.log('Loaded email data:', {
            tiersCount: Object.keys(ztier).length,
            emailListCount: emailList.length,
            sampleTiers: Object.values(ztier).slice(0, 3).map(t => ({ relId: t.relId, nickname: t.nickname }))
          });
          setITiers(ztier);
          setEmailList(emailList);
        }
      } else {
        console.log("Failed to get email list");
      }
    } catch (err) {
      console.log("Error getting email list", err);
    }
  };

  const submitForm = async (ev) => {
    ev.preventDefault();

    let warnings = [];

    if (!enableSubmitFlag || loading) return;

    console.log("submitting");
    setLoading(true);

    let collectionLength = 0;

    if (version === "new")
      collectionLength = Objectsize(selectedCollections);
    else collectionLength = Object.keys(selectedCollections).length;

    if (subject === "") {
      warnings.push("Looks like there's no subject.");
    }

    if (collectionLength === 0) {
      warnings.push("You have no collections selected.");
    }

    if (emails.length === 0) {
      warnings.push("You don't have any emails selected.");
    }

    if (warnings.length) {
      setLoading(false);

      warnings.forEach((item) => {
        toast.warn(
          item,
          "There are missing stuff in your distribution.",
          10000
        );
      });

      console.log("subject", subject);
      console.log("plength", collectionLength);
      console.log("emails", emails.length);
    } else {
      try {
        const token = props.token;

        if (time === "") setTime(Moment());

        // Set time appropriately based on schedule choice
        let finalTime;
        if (toggleSchedule) {
          finalTime = time.unix();
        } else {
          finalTime = Moment().unix(); // Current time for immediate send
        }

        console.log("message body", body);
        console.log("schedule time", finalTime, toggleSchedule ? "(scheduled)" : "(immediate)");

        // Prepare collections array - should be array of rel_ids for the API
        let collectionsArray = [];
        for (let [key, value] of Object.entries(selectedCollections)) {
          collectionsArray.push(value.relId);
        }

        const requestBody = {
          subject: subject,
          message: body, // API expects 'message' not 'body'
          collections: collectionsArray, // Array of rel_ids
          emails: emails,
          time: finalTime,
          viewOnly: Boolean(viewOnly),
        };

        console.log("Submitting distribution:", requestBody);

        const response = await DistributionsApi.addDistribution(token, requestBody);

        if (response && response.success) {
          console.log("Distribution response:", response);
          setWarning(true);
          setMessage("Successful Request.");
          setLoading(false);
          setModified(false);

          toast.success("Distribution Successfully started", "", 10000);
          navigate("/distributions");
        } else {
          setLoading(false);
          toast.warn("Failed to create distribution", "Something went wrong", 10000);
        }
      } catch (error) {
        console.warn("ASYNC  SUBMIT FAILURE", error);
        setLoading(false);
        toast.error(error, "Uh oh, something went wrong", 10000);
      }
    }
  };

  const handleTierChange = (color, id, name) => {
    setModified(true);

    console.log(`Selecting tier: ${name} (ID: ${id})`);

    // Skip processing for "No Tier" selection (tier 0)
    if (id === 0) {
      console.log("Skipping 'No Tier' selection");
      return;
    }

    // Collect emails from the selected tier and all its child tiers
    const collectTierEmails = (tierId, processedTiers = new Set()) => {
      // Prevent infinite recursion
      if (processedTiers.has(tierId)) {
        return [];
      }
      processedTiers.add(tierId);

      let allEmails = [];

      // Get emails from current tier
      const currentTierEmails = emailList
        .filter(email => parseInt(email.tier_rel_id) === parseInt(tierId))
        .map(email => email.email)
        .filter(email => !emails.includes(email)); // Only new emails

      allEmails = [...allEmails, ...currentTierEmails];

      // Find child tiers and collect their emails
      const childTiers = Object.values(iTiers).filter(tier =>
        parseInt(tier.parentTier) === parseInt(tierId) && tier.relId !== tierId
      );

      childTiers.forEach(childTier => {
        const childEmails = collectTierEmails(childTier.relId, processedTiers);
        allEmails = [...allEmails, ...childEmails];
      });

      return allEmails;
    };

    // Collect all emails from the selected tier and its children
    const tierEmails = collectTierEmails(id);

    // Add all collected emails at once
    if (tierEmails.length > 0) {
      const newEmails = [...emails, ...tierEmails];
      setEmails(newEmails);
      console.log(`Added ${tierEmails.length} emails from tier "${name}" and its child tiers:`, tierEmails);
    } else {
      console.log(`No new emails found for tier "${name}"`);
    }
  };

  const handleTemplateChange = (e) => {
    setSelectedTemplate(e.target.value);
    setTemplateName(templates[e.target.value][0]);
    setBody(templates[e.target.value][1]);
  };

  const handleSubjectChange = (e) => {
    setSubject(e.target.value);
    setModified(true);
  };

  const handleNoteChange = (e) => {
    setNotes(e.target.value);
    setModified(true);
  };

  const handleEditorChange = (content, editor) => {
    if (isMounted.current) {
      setBody(content);
      setModified(true);
    }
  };

  const handleCollectionChange = (e) => {
    if (e.target.value !== "0") {
      const selectedIndex = parseInt(e.target.value);
      const selectedItem = collections[selectedIndex];

      if (selectedItem && !selectedCollections[selectedItem.relId]) {
        const newCollections = { ...selectedCollections };
        newCollections[selectedItem.relId] = selectedItem;
        if (isMounted.current) {
          setSelectedCollections(newCollections);
        }
      }
      e.target.value = 0;
    }
  }; const handleCollectionChangeV3 = (e) => {
    if (e.target.value !== 0) {
      let val = JSON.parse(e.target.value);

      let collections = { ...selectedCollections };
      collections[val.relId] = val;

      if (isMounted.current) {
        setSelectedCollections(collections);
        // Auto-advance to step 2 and load emails for Individual mode
        if (monthlyMode === false && step < 2) {
          setStep(2);
          // Load emails for this collection's tier automatically
          let newEmails = [];
          emailList.forEach(email => {
            if (email.tier_rel_id === val.tier && !newEmails.includes(email.email)) {
              newEmails.push(email.email);
            }
          });
          setEmails(newEmails);
        }
      }

      e.target.value = 0;
    }
  };

  const enableSubmit = () => {
    setEnableSubmitFlag(true);
  };

  const disableSubmit = () => {
    setEnableSubmitFlag(false);
  };

  const handleManualEmail = (e) => {
    setManualEmail(e.target.value);
  };

  const submitManualEmail = (e) => {
    e.preventDefault();
    if (manualEmail !== "") {
      let newEmails = [...emails];

      if (!emails.includes(manualEmail)) {
        newEmails.push(manualEmail);
      }
      setEmails(newEmails);
      setManualEmail("");
    }
  };

  const removeEmail = (emailToRemove) => {
    const newEmails = emails.filter(email => email !== emailToRemove);
    setEmails(newEmails);
  };

  const removeCollection = (relId) => {
    let collections = { ...selectedCollections };
    delete collections[relId];

    if (isMounted.current) {
      setSelectedCollections(collections);
    }
  };

  const removeCollectionV3 = (relId) => {
    let collections = { ...selectedCollections };
    delete collections[relId];

    if (isMounted.current) {
      setSelectedCollections(collections);
    }
  };

  const handleGroupChange = (event) => {
    const selectedGroup = groupedSets[event.target.value];
    if (selectedGroup) {
      const sets = selectedGroup[1].map(s => ({ ...s, selected: false }));
      setActiveMonthSets(sets);
      setEmails([]);
      setSelectedCollections({});
      setStep(2);
    }
  };

  const handleSetFromGroupSelect = (index) => {
    const newActiveMonthSets = [...activeMonthSets];
    const setToToggle = newActiveMonthSets[index];
    setToToggle.selected = !setToToggle.selected;

    const newSelectedCollections = { ...selectedCollections };
    if (setToToggle.selected) {
      newSelectedCollections[setToToggle.relId] = setToToggle;
    } else {
      delete newSelectedCollections[setToToggle.relId];
    }

    setActiveMonthSets(newActiveMonthSets);
    setSelectedCollections(newSelectedCollections);
  };

  const Objectsize = function (obj) {
    var size = 0,
      key;
    for (key in obj) {
      if (obj.hasOwnProperty(key)) size++;
    }
    return size;
  };

  const processStep2 = () => {
    let newEmails = [];
    Object.values(selectedCollections).forEach(collection => {
      emailList.forEach(email => {
        if (email.tier_rel_id === collection.tier && !newEmails.includes(email.email)) {
          newEmails.push(email.email);
        }
      });
    });
    setEmails(newEmails);
    setStep(3);
  };

  return (
    <>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 4, border: "none", boxShadow: "none" }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4">Add Distribution</Typography>
          </Grid>
          <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button onClick={() => setVersion(version === "new" ? "old" : "new")}>
              {version === "new" ? "Switch to Advanced Mode" : "Switch to Simple Mode"}
            </Button>
            <HelpHint
              title="Add a Distribution"
              hints={[
                "Choose Monthly or Individual rewards",
                "Pick a tier to send to, or target a saved email list",
                "Use Simple Mode for the guided flow, Advanced for full control",
              ]}
            />
          </Grid>
        </Grid>

        {version === "new" ? (
          <Box>
            {!modeToggled && (
              <Paper elevation={2} sx={{ p: 2, my: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Which type of distribution do you want to create?</Typography>
                <ToggleButtonGroup
                  color="primary"
                  value={monthlyMode}
                  exclusive
                  onChange={(e, newMode) => {
                    if (newMode !== null) {
                      console.log('Mode selected:', newMode ? 'Monthly' : 'Individual');
                      setMonthlyMode(newMode);
                      setModeToggled(true);
                      setEmails([]);
                      setSelectedCollections({});
                      setStep(1);
                    }
                  }}
                  aria-label="distribution mode"
                  sx={{ width: '100%' }}
                >
                  <ToggleButton value={true} sx={{ flex: 1 }} data-testid="distribution-mode-monthly">Monthly Rewards</ToggleButton>
                  <ToggleButton value={false} sx={{ flex: 1 }} data-testid="distribution-mode-individual">Individual Rewards</ToggleButton>
                </ToggleButtonGroup>
              </Paper>
            )}

            {modeToggled && (
              <Paper elevation={2} sx={{ p: 2, my: 2 }}>
                <Typography variant="subtitle1">
                  Creating: {monthlyMode ? 'Monthly Rewards' : 'Individual Rewards'} Distribution
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setModeToggled(false);
                    setMonthlyMode(null);
                    setStep(0);
                    setEmails([]);
                    setSelectedCollections({});
                    setActiveMonthSets([]);
                  }}
                  sx={{ ml: 2 }}
                >
                  Change Mode
                </Button>
              </Paper>
            )}

            {step >= 1 && monthlyMode === true && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Paper elevation={2} sx={{ p: 2, my: 2 }}>
                  <Typography variant="h6">Step 1: Select Reward Month</Typography>
                  <FormControl fullWidth>
                    <InputLabel>Reward Month</InputLabel>
                    <Select onChange={handleGroupChange} defaultValue="">
                      {groupedSets.map((group, index) => {
                        // group[0] is like "g1234567890" where the number is the Unix timestamp
                        const timestamp = group[0] ? group[0].substr(1) : "0";
                        const monthText = timestamp !== "0"
                          ? Moment.unix(parseInt(timestamp)).format("MMMM YYYY")
                          : "No Month Set";

                        return (
                          <MenuItem key={index} value={index}>
                            {monthText} ({group[1]?.length || 0} collections)
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Paper>
              </motion.div>
            )}

            {step >= 1 && monthlyMode === false && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Paper elevation={2} sx={{ p: 2, my: 2 }}>
                  <Typography variant="h6">Step 1: Select collections</Typography>
                  <FormControl fullWidth>
                    <InputLabel>Collections</InputLabel>
                    <Select
                      onChange={handleCollectionChangeV3}
                      disabled={collectionDisabled}
                      defaultValue="0"
                      inputProps={{ 'data-testid': 'distribution-collections-select-simple' }}
                    >
                      <MenuItem value="0" disabled>
                        Select collection...
                      </MenuItem>
                      {(() => {
                        const owned = collections.filter((c) => !c.isLinked);
                        const linked = collections.filter((c) => c.isLinked === true);
                        return [
                          owned.length > 0 && <ListSubheader key="owned-header">Owned</ListSubheader>,
                          ...owned.map((i) => (
                            <MenuItem value={JSON.stringify(i)} key={`owned-${i.relId}`} data-testid={`distribution-collection-option-${i.relId}`}>
                              {i.title} (Tier: {iTiers[i.tier]?.nickname || i.tier})
                            </MenuItem>
                          )),
                          linked.length > 0 && <ListSubheader key="linked-header">Linked</ListSubheader>,
                          ...linked.map((i) => (
                            <MenuItem value={JSON.stringify(i)} key={`linked-${i.relId}`} data-testid={`distribution-collection-option-${i.relId}`}>
                              {i.title} — Linked: {i.linkType || "shared"} (Tier: {iTiers[i.tier]?.nickname || i.tier})
                            </MenuItem>
                          ))
                        ];
                      })()}
                    </Select>
                  </FormControl>
                </Paper>
              </motion.div>
            )}

            {step >= 2 && monthlyMode === true && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Paper elevation={2} sx={{ p: 2, my: 2 }}>
                  <Typography variant="h6">Step 2: Select Collections</Typography>
                  <Typography>
                    Choose the collections you want to distribute this month.
                  </Typography>
                  <Box sx={{ my: 2 }}>
                    {(() => {
                      const renderGroup = (sets) =>
                        sets.map((c) => {
                          const index = activeMonthSets.findIndex((x) => x.relId === c.relId);
                          return (
                            <Button
                              key={`${c.isLinked ? 'linked' : 'owned'}-${c.relId}`}
                              variant={c.selected ? "contained" : "outlined"}
                              onClick={() => handleSetFromGroupSelect(index)}
                              sx={{ m: 1 }}
                            >
                              {c.title}{c.isLinked ? ` — Linked: ${c.linkType || 'shared'}` : ''}
                            </Button>
                          );
                        });
                      const owned = activeMonthSets.filter((c) => !c.isLinked);
                      const linked = activeMonthSets.filter((c) => c.isLinked);
                      return [
                        owned.length > 0 && (
                          <Typography key="owned-header" variant="subtitle1" sx={{ mt: 1 }}>
                            Owned
                          </Typography>
                        ),
                        ...renderGroup(owned),
                        linked.length > 0 && (
                          <Typography key="linked-header" variant="subtitle1" sx={{ mt: 1 }}>
                            Linked
                          </Typography>
                        ),
                        ...renderGroup(linked),
                      ];
                    })()}
                  </Box>
                  {Object.keys(selectedCollections).length > 0 && (
                    <Button
                      variant="contained"
                      onClick={processStep2}
                      sx={{ float: "right" }}
                        data-testid="distribution-next-button-step2"
                    >
                      Next
                    </Button>
                  )}
                </Paper>
              </motion.div>
            )}

            {step >= 2 && monthlyMode === false && Object.keys(selectedCollections).length !== 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Paper elevation={2} sx={{ p: 2, my: 2 }}>
                  <Typography variant="h6">Step 2: Review Collections</Typography>
                  <Typography>
                    These are the collections you've selected. Click on a collection
                    name to remove it.
                  </Typography>
                  <Box sx={{ my: 2 }}>
                    {Object.values(selectedCollections).map((item, index) => (
                      <Button
                        key={item.relId}
                        variant="outlined"
                        onClick={() => removeCollectionV3(item.relId)}
                        sx={{ m: 1 }}
                      >
                        {item.title}
                      </Button>
                    ))}
                  </Box>
                  <Button
                    variant="contained"
                    onClick={processStep2}
                    sx={{ float: "right" }}
                    data-testid="distribution-next-button-step2"
                  >
                    Next
                  </Button>
                </Paper>
              </motion.div>
            )}

            {step >= 3 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Paper elevation={2} sx={{ p: 2, my: 2 }}>
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                      <Typography variant="h6">Step 3: Select Emails</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body2" color="textSecondary">
                        {emails.length} email{emails.length !== 1 && "s"} selected
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography>
                    These are the emails you're going to send your sets to. Click
                    an email address to remove it.
                  </Typography>

                  <Box
                    sx={{
                      my: 2,
                      maxHeight: 300,
                      overflowY: "auto",
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <List>
                      {emails.map((e, index) => (
                        <ListItem
                          key={index}
                          button
                          onClick={() => removeEmail(e)}
                        >
                          <ListItemText primary={e} />
                        </ListItem>
                      ))}
                      {emails.length === 0 && (
                        <ListItem>
                          <ListItemText
                            primary="No emails yet."
                            secondary="Add emails using the form below or by selecting tiers."
                            sx={{ textAlign: "center" }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Box>

                  <form onSubmit={submitManualEmail} style={{ marginTop: 16 }}>
                    <TextField
                      label="Add email manually"
                      variant="outlined"
                      fullWidth
                      value={manualEmail}
                      onFocus={disableSubmit}
                      onBlur={enableSubmit}
                      onChange={handleManualEmail}
                      sx={{ mb: 2 }}
                      inputProps={{ 'data-testid': 'distribution-manual-email-input' }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading || submitting}
                      fullWidth
                      data-testid="distribution-add-email-button"
                    >
                      {loading || submitting ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Add Email"
                      )}
                    </Button>
                  </form>

                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        or choose Tiers to select a group of emails
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TierDropdown
                        action={handleTierChange}
                        user={props.user}
                        token={props.token}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </motion.div>
            )}

            {emails.length > 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Paper elevation={2} sx={{ p: 2, my: 2 }}>
                  <Typography variant="h6">Step 4: Email Content</Typography>
                  <Typography>
                    Set the email content you're going to send to the emails.
                  </Typography>

                  <TextField
                    label="Subject"
                    variant="outlined"
                    fullWidth
                    inputProps={{ 'data-testid': 'distribution-subject-input' }}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                    sx={{ my: 2 }}
                  />

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Template</InputLabel>
                    <Select
                      onChange={handleTemplateChange}
                      value={selectedTemplate}
                      disabled={!templates.length}
                    >
                      {templates.map((i, index) => (
                        <MenuItem key={index} value={index} disabled={i[2]}>
                          {i[0]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Share Settings</Typography>
                    <ToggleButtonGroup
                      color="primary"
                      value={viewOnly ? 'viewOnly' : 'standard'}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          setViewOnly(newValue === 'viewOnly');
                        }
                      }}
                      aria-label="share access mode"
                      sx={{ width: '100%' }}
                    >
                      <ToggleButton value="standard" sx={{ flex: 1 }}>Standard (View + Download)</ToggleButton>
                      <ToggleButton value="viewOnly" sx={{ flex: 1 }}>View Only</ToggleButton>
                    </ToggleButtonGroup>
                    {viewOnly && (
                      <Alert severity="info" sx={{ mt: 1 }}>Recipients will be able to view content but not download it.</Alert>
                    )}
                  </Box>

                  <Editor
                    apiKey="er0sqezegew4ztzey6vtavdg77pjxd49j8l34vub9amk48c2"
                    value={body}
                    onEditorChange={(content) => setBody(content)}
                    init={{ height: 300, menubar: false, plugins: 'lists link', toolbar: 'undo redo | bold italic | bullist numlist' }}
                  />
                </Paper>
              </motion.div>
            )}

            {emails.length > 0 && subject !== "" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Paper elevation={2} sx={{ p: 2, my: 2 }}>
                  <Typography variant="h6">Step 5: Schedule Distribution</Typography>
                  <Typography sx={{ mb: 2 }}>
                    Send your distribution now or schedule it for later.
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <ToggleButtonGroup
                      color="primary"
                      value={toggleSchedule}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          setToggleSchedule(newValue);
                          if (!newValue) {
                            // If switching to "now", set time to current moment
                            setTime(Moment());
                          }
                        }
                      }}
                      aria-label="schedule mode"
                      sx={{ width: '100%', mb: 2 }}
                    >
                      <ToggleButton value={false} sx={{ flex: 1 }}>
                        Distribute Now
                      </ToggleButton>
                      <ToggleButton value={true} sx={{ flex: 1 }}>
                        Schedule for Later
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {toggleSchedule && (
                    <Box sx={{ mb: 3 }}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                          label="Schedule Time"
                          value={time}
                          onChange={(newValue) => setTime(newValue)}
                          slots={{
                            textField: TextField
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true
                            }
                          }}
                          minDateTime={Moment()}
                        />
                      </LocalizationProvider>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    data-testid="send-distribution-button"
                    onClick={submitForm}
                    disabled={loading || submitting}
                    fullWidth
                  >
                    {loading || submitting ? (
                      <CircularProgress size={24} />
                    ) : toggleSchedule ? (
                      "Schedule Distribution"
                    ) : (
                      "Distribute Now"
                    )}
                  </Button>
                </Paper>
              </motion.div>
            )}
          </Box>
        ) : (
          <Box>
            <Paper component="form" onSubmit={submitForm} elevation={2} sx={{ p: 2, my: 2 }}>
              <Typography variant="h6">Advanced Distribution Form</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    Choose collections to include in this distribution
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Collections</InputLabel>
                    <Select
                      onChange={handleCollectionChange}
                      disabled={collectionDisabled}
                      defaultValue="0"
                      inputProps={{ 'data-testid': 'distribution-collections-select-advanced' }}
                    >
                      <MenuItem value="0" disabled>
                        Select collection...
                      </MenuItem>
                      {(() => {
                        const owned = collections.filter((c) => !c.isLinked);
                        const linked = collections.filter((c) => c.isLinked === true);
                        return [
                          owned.length > 0 && <ListSubheader key="adv-owned-header">Owned</ListSubheader>,
                          ...owned.map((i) => (
                            <MenuItem value={collections.indexOf(i)} key={`adv-owned-${i.relId}`} data-testid={`distribution-collection-option-advanced-${i.relId}`}>
                              {i.title} (Tier: {iTiers[i.tier]?.nickname || i.tier})
                            </MenuItem>
                          )),
                          linked.length > 0 && <ListSubheader key="adv-linked-header">Linked</ListSubheader>,
                          ...linked.map((i) => (
                            <MenuItem value={collections.indexOf(i)} key={`adv-linked-${i.relId}`} data-testid={`distribution-collection-option-advanced-${i.relId}`}>
                              {i.title} — Linked: {i.linkType || "shared"} (Tier: {iTiers[i.tier]?.nickname || i.tier})
                            </MenuItem>
                          ))
                        ];
                      })()}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    Selected Collections
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {Object.values(selectedCollections).map((item, index) => (
                      <Chip
                        key={item.relId}
                        label={item.title}
                        onDelete={() => removeCollection(item.relId)}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    Selected Emails
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {emails.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {emails.map((email, index) => (
                          <Chip
                            key={index}
                            label={email}
                            onDelete={() => removeEmail(email)}
                            color="secondary"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No emails selected yet. Choose tiers or add manually below.
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    label="Add email manually"
                    variant="outlined"
                    fullWidth
                    value={manualEmail}
                    onChange={handleManualEmail}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        submitManualEmail(e);
                      }
                    }}
                    sx={{ mb: 2 }}
                    inputProps={{ 'data-testid': 'distribution-manual-email-input-advanced' }}
                  />
                  <Button
                    variant="outlined"
                    onClick={submitManualEmail}
                    disabled={!manualEmail}
                    sx={{ mb: 2 }}
                    data-testid="distribution-add-email-button-advanced"
                  >
                    Add Email
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    Choose Tiers to select a group of emails
                  </Typography>
                  <TierDropdown
                    action={handleTierChange}
                    user={props.user}
                    token={props.token}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1">Email Subject</Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1">Email Template</Typography>
                  <FormControl fullWidth>
                    <InputLabel>Template</InputLabel>
                    <Select
                      onChange={handleTemplateChange}
                      value={selectedTemplate}
                      disabled={!templates.length}
                    >
                      {templates.map((i, index) => (
                        <MenuItem key={index} value={index} disabled={i[2]}>
                          {i[0]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1">Email Body</Typography>
                  <Editor
                    apiKey="er0sqezegew4ztzey6vtavdg77pjxd49j8l34vub9amk48c2"
                    value={body}
                    onEditorChange={(content) => setBody(content)}
                    init={{ height: 300, menubar: false, plugins: 'lists link', toolbar: 'undo redo | bold italic | bullist numlist' }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1">Schedule Distribution</Typography>
                  <Box sx={{ mb: 2 }}>
                    <ToggleButtonGroup
                      color="primary"
                      value={toggleSchedule}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          setToggleSchedule(newValue);
                          if (!newValue) {
                            setTime(Moment());
                          }
                        }
                      }}
                      aria-label="schedule mode"
                      sx={{ width: '100%', mb: 2 }}
                    >
                      <ToggleButton value={false} sx={{ flex: 1 }}>
                        Distribute Now
                      </ToggleButton>
                      <ToggleButton value={true} sx={{ flex: 1 }}>
                        Schedule for Later
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {toggleSchedule && (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateTimePicker
                        label="Schedule Time"
                        value={time}
                        onChange={(newValue) => setTime(newValue)}
                        slots={{
                          textField: TextField
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true
                          }
                        }}
                        minDateTime={Moment()}
                      />
                    </LocalizationProvider>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Share Settings</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    value={viewOnly ? 'viewOnly' : 'standard'}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setViewOnly(newValue === 'viewOnly');
                      }
                    }}
                    aria-label="share access mode"
                    sx={{ width: '100%' }}
                  >
                    <ToggleButton value="standard" sx={{ flex: 1 }}>Standard (View + Download)</ToggleButton>
                    <ToggleButton value="viewOnly" sx={{ flex: 1 }}>View Only</ToggleButton>
                  </ToggleButtonGroup>
                  {viewOnly && (
                    <Alert severity="info" sx={{ mt: 1 }}>Recipients will be able to view content but not download it.</Alert>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || submitting || Object.keys(selectedCollections).length === 0 || emails.length === 0 || !subject}
                    fullWidth
                  >
                    {loading || submitting ? (
                      <CircularProgress size={24} />
                    ) : toggleSchedule ? (
                      "Schedule Distribution"
                    ) : (
                      "Distribute Now"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}
      </Paper>
    </>
  );
}

export default DistributionAdd;
